import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, of, shareReplay, tap } from 'rxjs';
import { EmailInboxType } from 'src/app/communications/models/contacts/email-inbox-type';
import { ScanEmailResponse } from 'src/app/job/models/email-scan-response.model';
import { Events } from 'src/app/shared/constants/events.constant';
import { environment } from 'src/environments/environment';
import { EmailSignature } from '../../../../job/models/job-detail/email/job-detail-email-signature.model';
import { OwnerApprovalDisclaimer } from '../../../../job/models/job-detail/email/job-detail-email-owner-approval-disclaimer.model';
import { EmailTemplate } from '../../../../job/models/job-detail/email/job-detail-email-template.model';
import { JobUser } from '../../../../job/models/job-detail/email/job-detail-email-user.model';
import { JobDetailEmail } from '../../../../job/models/job-detail/email/job-detail-email.model';
import { BaseService } from '../base/base.service';
import { CacheService } from '../cache.service';
import { EventService } from '../event.service';
import { EmailDownloadResponse } from 'src/app/communications/models/emails/email-download-responce.model';

@Injectable({
  providedIn: 'root',
})
export class EmailLogService extends BaseService {
  private readonly urlPrefix = `${environment.maintenance.urlPrefix}/job/email`;
  private readonly _userListAgencyUrl: string =
    '/agencymgmt/api/property/users'; //Verify and move
  private readonly _emailSignatureUrl: string =
    '/agencymgmt/api/document/signature'; //Verify and move
  private readonly _ownerApprovalDisclaimerUrl: string =
    '/agencymgmt/api/owner/ownerApproval-disclaimer';

  constructor(
    private httpClient: HttpClient,
    private cache: CacheService,
    private eventService: EventService
  ) {
    super(httpClient);
  }

  public getEmailListByJobId(jobId: string, version: string) {
    if (!jobId) return of();
    const queryParams = this.queryParams({
      jobId,
    });
    return this.cache
      .get(
        `${this.constructor.name}_${this.getEmailListByJobId.name}`,
        `${queryParams}${version}`,
        this.get<JobDetailEmail[]>(`${this.urlPrefix}/list${queryParams}`),
        [Events.JobEmailSentEvent]
      )
      .pipe(filter((resp) => resp != null));
  }

  downloadEmail(emailId: string, jobId: string) {
    let endpointUrl = `${this.urlPrefix}/download?EmailId=${emailId}&JobId=${jobId}`;
    return this.get<EmailDownloadResponse>(endpointUrl);
  }

  getEmailThread(jobId: string, emailId: string) {
    if (!jobId || !emailId) return of(new Array());
    let endpointUrl = `${this.urlPrefix}/thread?jobId=${jobId}&emailId=${emailId}`;
    return this.get<JobDetailEmail[]>(endpointUrl);
  }

  sendEmail(jobDetailEmail: JobDetailEmail) {
    return this.post<string>(
      `${this.urlPrefix}/sendemail`,
      jobDetailEmail
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailSentEvent);
      })
    );
  }

  replyEmail(jobDetailEmail: JobDetailEmail) {
    return this.post<string>(
      `${this.urlPrefix}/replyemail`,
      jobDetailEmail
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailSentEvent);
      })
    );
  }
  forwardEmail(jobDetailEmail: JobDetailEmail) {
    return this.post<string>(
      `${this.urlPrefix}/forwardemail`,
      jobDetailEmail
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailSentEvent);
      })
    );
  }
  getUsersFromAgency(propertyId?: string) {
    let endpointUrl = `${this._userListAgencyUrl}?propertyId=${propertyId}`;
    return this.get<JobUser[]>(endpointUrl);
  }
  getUsersFromAgencyForJobs(JobId: string) {
    let endpointUrl = `${this._userListAgencyUrl}?JobId=${JobId}`;
    return this.get<JobUser[]>(endpointUrl);
  }

  updateEmailReadStatus(emailModel: JobDetailEmail) {
    var endpointUrl = `${this.urlPrefix}/emailreadstatus`;
    return this.post(endpointUrl, emailModel);
  }

  updateEmailReadOrUnreadStatus(jobId: string, emailId: string) {
    let endpointUrl = `${this.urlPrefix}/emailreadorunreadstatus?jobId=${jobId}&emailId=${emailId}`;
    return this.post(endpointUrl, null);
  }

  updateEmailArchiveOrUnAchiveStatus(request: any) {
    let endpointUrl = `${this.urlPrefix}/emailarchiveorunachivestatus?emailId=${request.EmailId}&status=${request.Status}`;
    return this.post(endpointUrl, null);
  }

  public getEmailSignature(version: string) {
    let endpointUrl = this._emailSignatureUrl;
    return this.cache
      .get(
        `${this.constructor.name}_${this.getEmailSignature.name}`,
        `${version}`,
        this.get<EmailSignature>(endpointUrl),
        [Events.OrganizationSettingsChangedEvent]
      )
      .pipe(filter((resp) => resp != null));
  }

  public getOwnerApprovalDisclaimer(version: string) {
    let endpointUrl = this._ownerApprovalDisclaimerUrl;
    return this.cache
      .get(
        `${this.constructor.name}_${this.getOwnerApprovalDisclaimer.name}`,
        `${version}`,
        this.get<OwnerApprovalDisclaimer>(endpointUrl),
        [Events.OrganizationSettingsChangedEvent]
      )
      .pipe(filter((resp) => resp != null));
  }

  getEmailTemplates(version: string) {
    let endpointUrl = `${this.urlPrefix}/templates`;
    return this.cache
      .get(
        `${this.constructor.name}_${this.getEmailTemplates.name}`,
        `${version}`,
        this.get<EmailTemplate[]>(endpointUrl),
        [Events.JobEmailTemplateEvent]
      )
      .pipe(filter((resp) => resp != null));
  }

  addEmailTemplate(emailTemplate: EmailTemplate) {
    return this.post<string>(`${this.urlPrefix}/template`, emailTemplate).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailTemplateEvent);
      })
    );
  }

  updateEmailTemplate(emailTemplate: EmailTemplate) {
    return this.put<string>(`${this.urlPrefix}/template`, emailTemplate).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailTemplateEvent);
      })
    );
  }

  deleteEmailTemplate(templateId: string) {
    let endpointUrl = `${this.urlPrefix}/template?templateId=${templateId}`;
    return this.delete<string>(endpointUrl, {}).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEmailTemplateEvent);
      })
    );
  }

  scanEmail(file: any) {
    return this.post<ScanEmailResponse>(`${this.urlPrefix}/scan`, file);
  }

  getEmailListByUserIds(
    type: EmailInboxType,
    userIds: string,
    pageNo: number,
    searchKey: string = '',
    pageSize: number = 5
  ) {
    const body = {
      type: type,
      userIds: userIds,
      pageNo: pageNo,
      searchKey: searchKey,
      pageSize: pageSize,
    };
    return this.post<any>(`${this.urlPrefix}/get-email-list-by-userIds`, body);
  }

  getSelectedTeam() {
    return this.get<any>(
      `${this.urlPrefix}/selected-team`
    );
  }

  public updateSelectedTeam(selectedTeam: string[]) {
    let data = { selectedTeam: selectedTeam };
    return this.patch(
      `${this.urlPrefix}/selected-team`,
      data
    );
  }
}
